<template>
  <div class="bg-white shadow sm:rounded-lg">
    <!-- Email Details -->
    <div class="px-4 py-5 sm:p-6">
      <div class="flex justify-between items-start">
        <div>
          <h3 class="text-lg font-medium leading-6 text-gray-900">{{ email.subject }}</h3>
          <p class="mt-1 text-sm text-gray-500">
            To: {{ email.recipient_name }} ({{ email.recipient_email }})
          </p>
          <p class="text-sm text-gray-500">
            Sent: {{ formatDate(email.sent_at) }}
          </p>
        </div>
        <div class="flex space-x-3">
          <span 
            v-if="email.has_replies" 
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
          >
            {{ email.reply_count }} {{ email.reply_count === 1 ? 'Reply' : 'Replies' }}
          </span>
          <span 
            :class="[
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
              statusClasses[email.status] || 'bg-gray-100 text-gray-800'
            ]"
          >
            {{ email.status }}
          </span>
        </div>
      </div>
      
      <!-- Email Content -->
      <div class="mt-4 prose max-w-none">
        <div class="border rounded-lg p-4 bg-gray-50">
          <pre class="whitespace-pre-wrap text-sm">{{ email.body }}</pre>
        </div>
      </div>
      
      <!-- Follow-up Actions Section -->
      <div class="mt-4 border-t pt-4">
        <FollowUpActions 
          :email-id="email.id"
          :is-follow-up-email="isFollowUpEmail"
          :follow-up-count="followUpCount"
          :has-replies="email.has_replies"
          :status="email.status"
          @follow-up-action-completed="$emit('refresh')"
        />
      </div>
      
      <!-- Replies Section -->
      <div v-if="email.has_replies" class="mt-6">
        <h4 class="text-lg font-medium text-gray-900 mb-3">Replies ({{ email.reply_count }})</h4>
        
        <div class="space-y-4">
          <div v-for="reply in email.replies" :key="reply.id" class="border rounded-lg p-4">
            <div class="flex justify-between items-start">
              <div>
                <p class="font-medium">{{ reply.subject }}</p>
                <p class="text-sm text-gray-600">
                  <strong>From:</strong> {{ reply.from_email }}<br>
                  <strong>Received:</strong> {{ formatDate(reply.received_at) }}
                </p>
              </div>
              
              <div>
                <button 
                  @click="toggleReplyVisibility(reply.id)"
                  class="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                >
                  {{ expandedReplies.includes(reply.id) ? 'Hide Content' : 'Show Content' }}
                </button>
              </div>
            </div>
            
            <div v-if="expandedReplies.includes(reply.id)" class="mt-3 bg-gray-50 p-3 rounded-md">
              <pre class="whitespace-pre-wrap text-sm">{{ reply.body }}</pre>
            </div>
            <div v-else class="mt-3 bg-gray-50 p-3 rounded-md">
              <p class="text-sm">{{ truncateText(reply.body, 100) }}</p>
            </div>
            <ReplyAttachments :attachments="reply.attachments" />
          </div>
        </div>
      </div>
      <div v-else class="mt-6">
        <p class="text-sm text-gray-500">No replies yet</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { EmailMessage } from '@/types/email'
import FollowUpActions from './FollowUpActions.vue'
import ReplyAttachments from './ReplyAttachments.vue'

const props = defineProps<{
  email: EmailMessage
}>()

const expandedReplies = ref<string[]>([])

const statusClasses = {
  pending: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800'
}

const isFollowUpEmail = computed(() => props.email.is_follow_up || false)
const followUpCount = computed(() => props.email.follow_up_count || 0)

function formatDate(date: string | Date | undefined): string {
  if (!date) return 'Not sent'
  return new Date(date).toLocaleString()
}

function toggleReplyVisibility(replyId: string) {
  if (expandedReplies.value.includes(replyId)) {
    expandedReplies.value = expandedReplies.value.filter(id => id !== replyId)
  } else {
    expandedReplies.value.push(replyId)
  }
}

function truncateText(text: string | undefined, maxLength = 100): string {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}
</script>

<style scoped>
.prose {
  max-width: 100%;
}
</style> 